// filepath: apps/web/src/features/observability/widgets/StepDurationHistogram.tsx
//
// FR-UI-DASH-1 AC-1 widget 3 -- step-duration p50/p95/p99 histogram.
//
// Renders three horizontal bars (p50, p95, p99) with their
// millisecond values as text nodes. The values are server-emitted
// (FR-OBS-3); the SPA scales the bars to the p99 value (the
// largest of the three) so the bars are visually comparable.
//
// INV-9: numeric values rendered as text nodes.
// INV-2: server strips unauthorised fields; the SPA renders what
// it receives.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { DrilldownLink } from "../DrilldownLink";
import type { TenantMetricsSummary } from "../types";

export interface StepDurationHistogramProps {
  summary: TenantMetricsSummary;
  onDrillDown?: (params: { from: string; to: string; metric: string }) => void;
}

export function StepDurationHistogram({
  summary,
  onDrillDown,
}: StepDurationHistogramProps): React.ReactElement {
  const { t } = useTranslation();

  const p50 = summary.step_duration_p50_ms;
  const p95 = summary.step_duration_p95_ms;
  const p99 = summary.step_duration_p99_ms;
  const denom = Math.max(p99, 1);

  const toIso = new Date().toISOString();
  const fromIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  return (
    <article
      className="observability-widget observability-widget-step-duration"
      data-testid="observability-widget-step-duration"
      aria-labelledby="observability-widget-step-duration-title"
      data-tenant-scope="self"
    >
      <h2
        id="observability-widget-step-duration-title"
        className="observability-widget-title"
      >
        {t(
          "observability.dashboard.widget.step_duration.title",
          "Step duration percentiles",
        )}
      </h2>
      <dl className="observability-widget-percentiles">
        <HistogramRow
          label={t("observability.dashboard.widget.step_duration.p50", "p50")}
          valueMs={p50}
          denom={denom}
          metricId="step_duration_p50"
          testId="observability-widget-step-duration-p50"
        />
        <HistogramRow
          label={t("observability.dashboard.widget.step_duration.p95", "p95")}
          valueMs={p95}
          denom={denom}
          metricId="step_duration_p95"
          testId="observability-widget-step-duration-p95"
        />
        <HistogramRow
          label={t("observability.dashboard.widget.step_duration.p99", "p99")}
          valueMs={p99}
          denom={denom}
          metricId="step_duration_p99"
          testId="observability-widget-step-duration-p99"
        />
      </dl>
      <DrilldownLink
        from={fromIso}
        to={toIso}
        metric="step_duration_p99"
        onNavigate={onDrillDown}
        label={t(
          "observability.dashboard.widget.drill_down",
          "View failed instances",
        )}
      />
    </article>
  );
}

function HistogramRow({
  label,
  valueMs,
  denom,
  testId,
}: {
  label: string;
  valueMs: number;
  denom: number;
  metricId: string;
  testId: string;
}): React.ReactElement {
  const widthPct = Math.min(100, Math.max(0, (valueMs / denom) * 100));
  return (
    <div className="observability-widget-percentile-row" data-testid={testId}>
      <dt className="observability-widget-percentile-label">{label}</dt>
      <dd className="observability-widget-percentile-bar-wrapper">
        <div
          className="observability-widget-percentile-bar"
          style={{ width: `${widthPct.toFixed(2)}%` }}
        />
        <span className="observability-widget-percentile-value">
          {valueMs}
          {" ms"}
        </span>
      </dd>
    </div>
  );
}
